import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { dateToDateOnly } from "@/lib/format";
import { useCreateHoliday } from "../../hooks/use-create-holiday";
import {
	type HolidayFormValues,
	holidaySchema,
} from "../../schemas/time-settings-schema";
import { timeSettingsErrorMessage, violationMessage } from "../../utils/errors";

const HOLIDAY_ALREADY_EXISTS = "holiday-already-exists";

const API_FIELDS: Record<string, keyof HolidayFormValues> = {
	date: "date",
	name: "name",
};

export function HolidayDialog({
	open,
	onOpenChange,
	year,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	year: number;
	onCreated: (year: number) => void;
}) {
	const createHoliday = useCreateHoliday();
	const [formError, setFormError] = useState<string | null>(null);

	const {
		control,
		register,
		handleSubmit,
		reset,
		setError,
		formState: { errors },
	} = useForm<HolidayFormValues>({
		resolver: zodResolver(holidaySchema),
		defaultValues: { date: undefined, name: "" },
	});

	useEffect(() => {
		if (!open) return;
		reset({ date: undefined, name: "" });
		setFormError(null);
	}, [open, reset]);

	async function onSubmit(values: HolidayFormValues) {
		setFormError(null);
		try {
			const holiday = await createHoliday.mutateAsync({
				date: dateToDateOnly(values.date),
				name: values.name,
			});
			toast.success("Feriado adicionado");
			onCreated(Number(holiday.date.slice(0, 4)) || year);
			onOpenChange(false);
		} catch (err) {
			if (err instanceof APIError) {
				if (err.reason === HOLIDAY_ALREADY_EXISTS) {
					setError("date", {
						message: timeSettingsErrorMessage(err, ""),
					});
					return;
				}
				let matched = false;
				for (const [field, description] of Object.entries(err.violations)) {
					const formField = API_FIELDS[field];
					if (!formField) continue;
					matched = true;
					setError(formField, { message: violationMessage(description) });
				}
				if (!matched) {
					setFormError(
						timeSettingsErrorMessage(
							err,
							"Não foi possível adicionar o feriado.",
						),
					);
				}
				return;
			}
			setFormError("Não foi possível adicionar o feriado. Tente novamente.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="holiday-dialog">
				<DialogHeader>
					<DialogTitle>Adicionar feriado</DialogTitle>
					<DialogDescription>
						Feriado válido apenas para este canteiro. Nos feriados não há horas
						esperadas e as horas trabalhadas contam como extra.
					</DialogDescription>
				</DialogHeader>

				<form
					id="holiday-form"
					className="holiday-dialog__form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="form-group">
						<Label htmlFor="holiday-date">Data</Label>
						<Controller
							control={control}
							name="date"
							render={({ field }) => (
								<DatePicker
									id="holiday-date"
									modal
									selected={field.value}
									onSelected={field.onChange}
									aria-invalid={!!errors.date}
								/>
							)}
						/>
						{errors.date && <p className="form-error">{errors.date.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="holiday-name">Nome</Label>
						<Input
							id="holiday-name"
							placeholder="Ex.: Aniversário da cidade"
							autoComplete="off"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					{formError && (
						<div className="form-error-alert" role="alert">
							{formError}
						</div>
					)}
				</form>

				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={createHoliday.isPending}
					>
						Cancelar
					</Button>
					<Button
						type="submit"
						form="holiday-form"
						disabled={createHoliday.isPending}
					>
						{createHoliday.isPending && (
							<Loader2 className="holiday-dialog__spinner" />
						)}
						Adicionar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
